import React, { useState, useEffect, useRef, Children, isValidElement } from 'react';
import { Switch, matchPath, useLocation } from 'react-router-dom';
import TopLoadingBar from './TopLoadingBar';

// Hard ceiling on how long we'll keep the bar on screen. Even if a chunk hangs
// or a `.preload()` promise never settles, the UI must recover.
const MAX_LOADING_MS = 12000;

const findMatchingRoute = (children, pathname) => {
    let match = null;
    Children.forEach(children, (child) => {
        if (match || !isValidElement(child)) return;
        const { path, exact, strict } = child.props || {};
        if (!path) return;
        const paths = Array.isArray(path) ? path : [path];
        for (const p of paths) {
            if (matchPath(pathname, { path: p, exact, strict })) {
                match = child;
                return;
            }
        }
    });
    return match;
};

const sameLocation = (a, b) =>
    a.pathname === b.pathname && a.search === b.search && a.hash === b.hash;

/**
 * Drop-in replacement for `<Switch>` that keeps the currently displayed route
 * mounted while the next route's lazy chunk downloads. When the chunk is
 * ready, it swaps to the new route. A thin top loading bar is shown during
 * the transition.
 *
 * Routes whose `component` is a `lazyWithPreload(...)` get the deferred-swap
 * behavior. Routes without a `.preload()` swap immediately.
 */
const PendingSwitch = ({ children }) => {
    const location = useLocation();
    const [displayed, setDisplayed] = useState(location);
    const [loading, setLoading] = useState(false);
    // Monotonic id for the most recently requested navigation. Any in-flight
    // preload whose id !== current is stale and ignored.
    const navIdRef = useRef(0);
    const safetyTimerRef = useRef(null);

    const stopLoading = () => {
        if (safetyTimerRef.current) {
            clearTimeout(safetyTimerRef.current);
            safetyTimerRef.current = null;
        }
        setLoading(false);
    };

    useEffect(() => {
        if (sameLocation(location, displayed)) return;

        const matched = findMatchingRoute(children, location.pathname);
        const Component = matched && matched.props && matched.props.component;
        const preload = Component && typeof Component.preload === 'function'
            ? Component.preload
            : null;

        if (!preload) {
            // Eager / unknown route — swap immediately.
            stopLoading();
            setDisplayed(location);
            return;
        }

        // Chunk already downloaded — swap instantly, no loading bar flash.
        if (Component.loaded === true) {
            stopLoading();
            setDisplayed(location);
            return;
        }

        const myNavId = ++navIdRef.current;
        setLoading(true);

        // Safety net: clear loading state no matter what after MAX_LOADING_MS.
        if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
        safetyTimerRef.current = setTimeout(() => {
            if (navIdRef.current === myNavId) {
                setDisplayed(location);
                stopLoading();
            }
        }, MAX_LOADING_MS);

        const settle = () => {
            // Only the latest navigation is allowed to commit.
            if (navIdRef.current !== myNavId) return;
            setDisplayed(location);
            stopLoading();
        };

        // Wrap in Promise.resolve in case `preload` returns a non-promise.
        Promise.resolve()
            .then(() => preload())
            .then(settle, settle);
    }, [location, displayed, children]);

    // Cleanup on unmount.
    useEffect(() => () => {
        if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    }, []);

    return (
        <>
            <TopLoadingBar active={loading} />
            <Switch location={displayed}>{children}</Switch>
        </>
    );
};

export default PendingSwitch;
